import type { AppScreen } from '../lib/query.js';
import { Button, Icon, ThemeToggle } from '@chessmate/ui';
import { ChevronRight, Home, Users, Wifi, GraduationCap, Trophy } from 'lucide-react';
import { edition } from '../edition/index.js';
import { useT } from '../i18n/useT.js';
import { useUiStore } from '../state/ui-store.js';
import { UserMenu } from './UserMenu.js';

/**
 * Global topbar. Renders once at the App root, sitting above every screen
 * EXCEPT the onboarding wizard and the story harness — those have their own
 * minimal chrome (full-bleed, no persistent chrome) and shouldn't carry this.
 *
 * Its chrome (height, padding, icon buttons) is fixed px, not rem: at a large system text size only the
 * text grows, so the bar stays one 56px row (plus the iOS status-bar inset, 0 in a browser) instead of wrapping into a tall sticky block. The breadcrumb
 * truncates first.
 *
 * Composition:
 *  - Left: logo + brand title (together the home link), then the breadcrumb of sub-screen context only
 *    (Game · Kitten, Learn > Lesson). Below 640px the title text is hidden (logo only) so the bar stays one row.
 *  - Right cluster: Home / Learn / Online / Leaderboard / Parents (identical NavIcons; the current screen's
 *    item carries aria-current="page"), the theme toggle, the user menu, and a collapse chevron for the in-game sidebar.
 *
 * The collapse chevron only renders on the in-game screens where the
 * collapsible sidebar lives; everywhere else the player has no sidebar to
 * collapse, so the button would be a phantom control.
 */
export interface TopbarProps {
  readonly screen: AppScreen;
  /** Active robot level for the `/game` breadcrumb; null elsewhere. */
  readonly levelName: string | null;
  readonly onHome: () => void;
  readonly onLearn: () => void;
  readonly onOnline: () => void;
  readonly onLeaderboard: () => void;
  readonly onParent: () => void;
  /** Open the sound settings (user menu). */
  readonly onSettings: () => void;
}

export function Topbar({ screen, levelName, onHome, onLearn, onOnline, onLeaderboard, onParent, onSettings }: TopbarProps) {
  const { t } = useT('navigation');
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  const crumb = crumbFor(screen, t, levelName);
  const active = activeNav(screen);

  return (
    <header
      className="sticky top-0 z-40 flex h-[calc(56px+env(safe-area-inset-top))] items-center justify-between gap-[8px] border-b border-sun/20 bg-cream/85 px-[8px] pt-[env(safe-area-inset-top)] backdrop-blur-md sm:gap-[12px] sm:px-[16px] lg:h-[calc(72px+env(safe-area-inset-top))] lg:px-[32px] dark:border-sun/10"
      data-testid="topbar"
      data-appdom-id="topbar"
      data-appdom-type="Topbar"
    >
      <div className="flex min-w-0 shrink-0 items-center gap-[8px] overflow-hidden sm:shrink sm:gap-[12px]">
        <button
          type="button"
          onClick={onHome}
          data-testid="topbar-home"
          data-appdom-id="topbar-home"
          data-appdom-type="Button"
          aria-label={`${edition.brand.name}, ${t('home')}`}
          className="inline-flex h-[48px] shrink-0 items-center gap-[10px] rounded-xl text-berry transition-colors hover:bg-berry/10"
        >
          <span className="inline-flex size-[48px] shrink-0 items-center justify-center rounded-xl bg-berry/15">
            <img src={edition.brand.logo} alt="" width={22} height={22} className="size-[24px] lg:size-[28px]" />
          </span>
          <span className="hidden whitespace-nowrap pr-[8px] font-display text-lg font-extrabold tracking-tight text-ink sm:inline lg:text-xl" data-testid="topbar-brand-name">
            {edition.brand.name}
          </span>
        </button>
        {crumb.length > 0 && (
          <div className="flex min-w-0 items-center gap-1 text-sm font-bold text-ink/70 max-sm:hidden lg:text-base">
            {crumb.map((c) => (
              <span key={c.key} className="flex min-w-0 items-center gap-1">
                <ChevronRight size={12} className="shrink-0 text-ink/30" />
                <span className="truncate font-extrabold text-ink">{c.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <nav aria-label={t('topbar-app-name')} className="flex shrink-0 items-center gap-[2px] sm:gap-[4px] lg:gap-[8px]">
        {/* The brand logo is the home link on a phone, so the second Home icon only shows from sm up (7 buttons at 48px fit 390px; 8 do not). */}
        <NavIcon testid="topbar-home-nav" label={t('home')} onClick={onHome} current={active === 'home'} hideOnPhone>
          <Home size={20} />
        </NavIcon>
        <NavIcon testid="topbar-learn" label={t('learn')} onClick={onLearn} current={active === 'learn'}>
          <GraduationCap size={20} />
        </NavIcon>
        <NavIcon testid="topbar-online" label={t('play-online')} onClick={onOnline} current={active === 'online'}>
          <Wifi size={20} />
        </NavIcon>
        <NavIcon testid="topbar-leaderboard" label={t('leaderboard')} onClick={onLeaderboard} current={active === 'leaderboard'}>
          <Trophy size={20} />
        </NavIcon>
        <NavIcon testid="topbar-parent" label={t('for-parents')} onClick={onParent} current={active === 'parent'}>
          <Users size={20} />
        </NavIcon>
        <span className="inline-flex shrink-0 [&>button]:size-[48px]">
          <ThemeToggle />
        </span>
        <UserMenu onSettings={onSettings} onParent={onParent} onOnline={onOnline} />
        {SHOW_COLLAPSE_ON.has(screen) && (
          <button
            type="button"
            data-testid="sidebar-collapse"
            data-appdom-id="sidebar-collapse"
            data-appdom-type="Button"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={sidebarCollapsed}
            onClick={toggleSidebar}
            className="ml-[2px] inline-flex max-sm:hidden size-[48px] items-center justify-center rounded-lg border-2 border-sun/40 bg-cream text-ink transition-colors hover:bg-sun/30 dark:border-sun/30 sm:ml-[4px]"
          >
            <Icon name="ChevronRight" size="sm" className={sidebarCollapsed ? '' : 'rotate-180'} />
          </button>
        )}
      </nav>
    </header>
  );
}

type NavId = 'home' | 'learn' | 'online' | 'leaderboard' | 'parent';

/** Which right-cluster item stands for the current screen (none for a game, the games page, settings). */
function activeNav(screen: AppScreen): NavId | null {
  switch (screen) {
    case 'home':
      return 'home';
    case 'learn':
    case 'lesson':
      return 'learn';
    case 'online-lobby':
    case 'online-game':
      return 'online';
    case 'leaderboard':
      return 'leaderboard';
    case 'parent':
      return 'parent';
    default:
      return null;
  }
}

function NavIcon({ testid, label, onClick, current, hideOnPhone = false, children }: { readonly testid: string; readonly hideOnPhone?: boolean; readonly label: string; readonly onClick: () => void; readonly current: boolean; readonly children: React.ReactNode }) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-appdom-id={testid}
      data-appdom-type="Button"
      aria-label={label}
      title={label}
      aria-current={current ? 'page' : undefined}
      onClick={onClick}
      className={`inline-flex h-[48px] w-[48px] items-center justify-center gap-2 rounded-xl border-2 transition-colors lg:w-auto lg:px-4 ${
        hideOnPhone ? 'max-sm:hidden ' : ''}${
        current ? 'border-berry/50 bg-berry/15 text-berry dark:border-berry/60' : 'border-sun/40 bg-cream text-ink hover:bg-sun/30 dark:border-sun/30'
      }`}
    >
      {children}
      <span className="hidden text-base font-bold xl:inline">{label}</span>
    </button>
  );
}

interface Crumb {
  readonly key: string;
  readonly label: string;
}

/**
 * Maps the active screen to a breadcrumb chain. Returns an empty array for
 * the home screen — the brand link alone is enough context there.
 *
 * The level name on `/game` rides on the second crumb so the player always
 * knows which robot they're up against without needing the in-game sidebar.
 */
function crumbFor(screen: AppScreen, t: (k: string) => string, levelName: string | null): readonly Crumb[] {
  switch (screen) {
    case 'game':
      return [{ key: 'game', label: levelName ? `${t('game')} · ${levelName}` : t('game') }];
    case 'games':
      return [{ key: 'games', label: t('games') }];
    case 'learn':
      return [{ key: 'learn', label: t('learn') }];
    case 'lesson':
      return [
        { key: 'learn', label: t('learn') },
        { key: 'lesson', label: t('lesson') },
      ];
    case 'online-lobby':
      return [{ key: 'online-lobby', label: t('online-lobby') }];
    case 'online-game':
      return [
        { key: 'online-lobby', label: t('online-lobby') },
        { key: 'online-game', label: t('online-game') },
      ];
    case 'lobby':
      return [{ key: 'lobby', label: t('lobby') }];
    case 'leaderboard':
      return [{ key: 'leaderboard', label: t('leaderboard') }];
    case 'parent':
      return [{ key: 'parent', label: t('parents-dashboard') }];
    case 'settings':
      return [{ key: 'settings', label: t('settings') }];
    case 'home':
    case 'story-harness':
      return [];
  }
}

/**
 * Screens that host the collapsible sidebar. The collapse chevron in the
 * topbar only renders on these so it never appears as a phantom control on
 * menus / lessons / story-harness.
 */
const SHOW_COLLAPSE_ON: ReadonlySet<AppScreen> = new Set<AppScreen>(['game', 'online-game']);
